/**
 * Service pour communiquer avec WaterLink Connect via Electron
 * Ce service utilise les canaux IPC d'Electron pour communiquer avec le processus principal
 */

import * as devSimulation from './devSimulation';

class ElectronWaterLinkService {
  constructor() {
    this.isElectron = window.require && window.require('electron');
  }

  /**
   * Vérifier si l'application s'exécute dans Electron
   *
   * En développement uniquement, la clé localStorage DEV_SIMULATE_ELECTRON
   * force la valeur retournée (voir components/DevTools/DevElectronSwitch).
   * En production, process.env.NODE_ENV vaut 'production' et ce bloc est
   * supprimé du bundle au build.
   */
  isElectronApp() {
    const simule = devSimulation.lireReglage(devSimulation.CLE_ELECTRON);
    if (simule !== null) {
      return simule;
    }
    return !!(window.electronAPI && window.electronAPI.isElectron);
  }

  /**
   * Faire un appel API via Electron IPC
   * @param {string} endpoint - Endpoint de l'API WaterLink
   * @param {string} method - Méthode HTTP (GET, POST)
   * @param {Object} data - Données à envoyer (optionnel)
   */
  async makeApiCall(endpoint, method = 'GET', data = null) {
    if (!this.isElectronApp()) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }

    if (!window.electronAPI) {
      throw new Error('API Electron non disponible');
    }

    try {
      const result = await window.electronAPI.waterlinkApi(endpoint, method, data);
      return result;
    } catch (error) {
      console.error('Erreur IPC:', error);
      throw new Error('Erreur de communication avec le processus principal');
    }
  }

  /**
   * Vérifier si le service WaterLink Connect est disponible
   */
  async checkService() {
    // La simulation passe avant le test de window.electronAPI : en developpement
    // le mode Electron est simule, l'IPC n'existe donc pas.
    if (devSimulation.estActive()) {
      return devSimulation.checkServiceSimule();
    }
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.checkService();
  }

  /**
   * Obtenir le statut de l'appareil connecté
   */
  async getStatus() {
    if (devSimulation.estActive()) {
      return devSimulation.getStatusSimule();
    }
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.getStatus();
  }

  /**
   * Obtenir les options de test disponibles
   * @param {string} version - Version du test (pool, spa, drinkingwater, coolingwater)
   */
  async getOptions(version = null) {
    if (devSimulation.estActive()) {
      return devSimulation.getOptionsSimule();
    }
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.getOptions(version);
  }

  /**
   * Lancer un test d'eau
   * @param {number} reagent - Type de réactif
   * @param {number} sample - Type d'échantillon
   */
  async runWaterTest(reagent, sample) {
    if (devSimulation.estActive()) {
      return devSimulation.runWaterTestSimule(reagent, sample);
    }
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.runTest(reagent, sample);
  }

  /**
   * Obtenir les logs de l'appareil
   */
  async getLogs() {
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.getLogs();
  }

  /**
   * Mettre à jour le firmware de l'appareil
   */
  async updateFirmware() {
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.updateFirmware();
  }

  /**
   * Calibrer l'appareil
   */
  async calibrate() {
    if (!this.isElectronApp() || !window.electronAPI) {
      throw new Error('Cette fonctionnalité nécessite Electron');
    }
    return await window.electronAPI.calibrate();
  }

  /**
   * Obtenir le texte d'état lisible
   * @param {number} status - Code de statut
   */
  getStatusText(status) {
    const statusMap = {
      0: 'Hors ligne',
      1: 'Occupé',
      2: 'Mise à jour',
      3: 'Erreur',
      4: 'Mise à jour requise',
      5: 'Prêt'
    };
    return statusMap[status] || 'Inconnu';
  }

  /**
   * Obtenir la couleur du statut
   * @param {number} status - Code de statut
   */
  getStatusColor(status) {
    const colorMap = {
      0: 'default',
      1: 'processing',
      2: 'warning',
      3: 'error',
      4: 'error',
      5: 'success'
    };
    return colorMap[status] || 'default';
  }

  /**
   * Obtenir le nom du facteur de test
   * @param {number} factorCode - Code du facteur
   */
  getFactorName(factorCode) {
    const factorMap = {
      1: 'Lumière absorbée',
      2: 'Chlore libre',
      3: 'Chlore total',
      4: 'Chlore combiné',
      5: 'Brome',
      6: 'Biguanide',
      7: 'Biguanide Shock',
      8: 'pH',
      9: 'Alcalinité / Dureté carbonatée',
      10: 'Dureté totale',
      11: 'Dureté calcique',
      12: 'Acide cyanurique',
      13: 'Fer total',
      14: 'Fer ferrique',
      15: 'Fer ferreux',
      16: 'Cuivre',
      17: 'Borate',
      18: 'Phosphate',
      19: 'Sel',
      20: 'Nitrate',
      21: 'Nitrite',
      22: 'Urée',
      23: 'Turbidité',
      24: 'Aluminium',
      25: 'Baryum',
      26: 'Benzotriazole',
      27: 'Bore',
      28: 'Cadmium',
      29: 'Carbohydrazide',
      30: 'Chlorinediox',
      31: 'Chlorure',
      32: 'Chrome',
      33: 'Cobalt',
      34: 'COD',
      35: 'Couleur',
      36: 'Cyanure',
      37: 'DEHA',
      38: 'Oxygène',
      39: 'Erythorbicacid',
      40: 'Fluorure',
      41: 'Hydrazine',
      42: 'Peroxyde',
      43: 'Hydroquinone',
      44: 'Iode',
      45: 'Plomb',
      46: 'Manganèse',
      47: 'Mercure',
      48: 'Ketoxime',
      49: 'Molybdène',
      50: 'Nickel',
      51: 'Oxydant',
      52: 'Ozone',
      53: 'Phénol',
      54: 'Ammoniac',
      55: 'Solides dissous totaux',
      56: 'Potassium',
      57: 'Silice',
      58: 'Tensioactifs',
      59: 'Sulfure',
      60: 'Sulfate',
      61: 'Tanin',
      62: 'Indice de saturation',
      63: 'Température',
      64: 'Alcalinité ajustée',
      65: 'Phosphore',
      66: 'Zinc',
      67: 'Magnésium',
      68: 'CO2',
      69: 'Ions magnésium',
      70: 'Ions calcium'
    };
    return factorMap[factorCode] || `Facteur ${factorCode}`;
  }
}

export default new ElectronWaterLinkService();
